import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { cdFetch } from "../api";
import { queryKeys } from "../queryKeys";
import {
  NegotiationFilterRes,
  QuoteSummaryRes,
  RenegotiateVendorDetailsData,
} from "../../services/interfaces/IVendorQuoteAssessment";
import {
  metaDataPvcConduit,
  metaDataSteelConduit,
} from "../../services/interfaces/IQuoteUpload";
import { getMonthTimestamp } from "../../utils/formatter";

const normalize = (input: Array<string> | null) => {
  if (!Array.isArray(input) || input.length === 0) return null;
  return input.slice().sort();
};

const convertMonthYearToDate = (monthYear: string): string => {
  const monthMap: any = {
    Jan: "01",
    January: "01",
    Feb: "02",
    February: "02",
    Mar: "03",
    March: "03",
    Apr: "04",
    April: "04",
    May: "05",
    Jun: "06",
    June: "06",
    Jul: "07",
    July: "07",
    Aug: "08",
    August: "08",
    Sep: "09",
    September: "09",
    Oct: "10",
    October: "10",
    Nov: "11",
    November: "11",
    Dec: "12",
    December: "12",
  };

  const [month, year] = monthYear.split(" ");
  const monthNum = monthMap[month];
  if (!monthNum || !year) return "";
  // Support both 4-digit ("July 2026") and 2-digit ("Jul 26") year formats.
  const fullYear = year.length === 2 ? `20${year}` : year;
  return `${fullYear}-${monthNum}-01`;
};


export const convertMmDdYyToDate = (mmDdYy: string): string => {
  const match = mmDdYy.trim().match(/^(\d{1,2})-(\d{1,2})-(\d{2})$/);
  if (!match) return "";

  const mm = Number(match[1]);
  const dd = Number(match[2]);
  const yy = Number(match[3]);

  const year = yy >= 70 ? 1900 + yy : 2000 + yy;

  // Validate the date to avoid rollover (e.g., 02-30-24)
  const d = new Date(Date.UTC(year, mm - 1, dd));
  const isValid =
    d.getUTCFullYear() === year &&
    d.getUTCMonth() === mm - 1 &&
    d.getUTCDate() === dd;

  if (!isValid) return "";

  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${year}-${pad(mm)}-${pad(dd)}`;
};

export const convertYyyyMmDdToDate = (mmDdYy: string): string => {
  const match = mmDdYy.trim().match(/^(\d{1,2})-(\d{1,2})-(\d{2})$/);
  if (!match) return "";

  const mm = Number(match[1]);
  const dd = Number(match[2]);
  const yy = Number(match[3]);

  const year = yy >= 70 ? 1900 + yy : 2000 + yy;

  const d = new Date(Date.UTC(year, mm - 1, dd));
  const isValid =
    d.getUTCFullYear() === year &&
    d.getUTCMonth() === mm - 1 &&
    d.getUTCDate() === dd;

  if (!isValid) return "";

  const pad = (n: number) => n.toString().padStart(2, "0");

  // return as DD-MM-YYYY
  return `${year}-${pad(mm)}-${pad(dd)}`;
};


const getQueryParams = (
  commodity: string | null,
  district: string[] | null,
  vendor: string[] | null,
  quotes: any = null,
  type: string | null = null,
  type_2: string | null = null,
  monthYear: string | null = null,
  contractTypes?: string[] | null,
  contractValidity?: string[] | null,
  benchmark?: string[] | null | string,
  navItem?: string | null,
) => {
  console.log(monthYear, "month");

  let qParams = "";
  if (commodity != null && commodity.length > 0) {
    qParams += `commodity=${JSON.stringify([commodity])}&`;
  }

  if (commodity != null && commodity === 'Copper Wire') {
    qParams += `module=wire&`;
  }

  if (navItem != null && navItem === 'districtComparison') {
    qParams += `tab=${JSON.stringify(navItem)}&`;
  }

  if (contractTypes && contractTypes.length > 0) {
    if (navItem === 'districtComparison' && commodity === 'Copper Wire') {
      // District comparison expects a single quote_type. The combined display value
      // "Firm/Float" (or "Firm,Float") is not valid here, so default it to "Firm".
      const rawQuoteType = contractTypes[0];
      const quoteType =
        rawQuoteType === "Firm/Float" || rawQuoteType === "Firm,Float"
          ? "Firm"
          : rawQuoteType;
      qParams += `quote_type=${JSON.stringify(quoteType)}&`;
    } else {
      const expanded = contractTypes.flatMap((t) => t === "Firm/Float" || t === "Firm,Float" ? ["Firm", "Float"] : [t?.split(" ")[0]]);
      qParams += `contract_type=${JSON.stringify(expanded)}&`;
    }
  }

  if (benchmark && benchmark.length > 0) {
    qParams += (navItem === 'districtComparison' && commodity === 'Copper Wire') ? `base_sku=${JSON.stringify(benchmark)}&` : `benchmark=${JSON.stringify(benchmark)}&`;
  }

  if (contractValidity && contractValidity.length > 0) {
    qParams += `contract_validity=${JSON.stringify(contractValidity)}&`;
  }

  if (district && district.length > 0) {
    qParams += `district=${JSON.stringify(district)}&`;
  }

  if (vendor && vendor.length > 0) {
    qParams += `vendor=${JSON.stringify(vendor)}&`;
  }

  if (quotes && quotes.length > 0) {
    qParams += `quotation_no=${JSON.stringify(quotes)}&`;
  }

  if (type) {
    qParams += `type=${type}&`;
  }

  if (type_2) {
    qParams += `type_2=${type_2}&`;
  }

  if (monthYear && monthYear?.length > 0) {
    const isCopperMonthFilter =
      commodity === "Copper Wire" &&
      (navItem === "sKURatioAnalysis" || navItem === "quoteSummary");

    const isFirmOrFloatContract =
      contractTypes?.some((value) => value === "Firm" || value === "Float" || value === "Firm,Float" || value === "Firm/Float") ?? false;

    qParams += (navItem === 'districtComparison' && commodity === 'Copper Wire') ?
      `month_year=${convertMonthYearToDate(monthYear)}&` :
      (isCopperMonthFilter && isFirmOrFloatContract) ?
        `month_year=${convertMonthYearToDate(monthYear)}&` :
      isCopperMonthFilter ?
        `month_year=${convertYyyyMmDdToDate(monthYear)}&` :
        `month_year=${convertMonthYearToDate(monthYear)}&`;
  }

  if (qParams.length > 0) {
    qParams = "?" + qParams.slice(0, -1);
  }

  console.log(qParams)
  return qParams;
};

export const useNegotiationFilters = (
  userEmail: string | null,
  commodity: string | null,
  district: string[] | null,
  vendor: string[] | null,
  monthYear: string | null,
  contractTypes: string[] | null,
  contractValidity: string[] | null,
  benchmark: string[] | null,
  quotes: string[] | null,
  navItem: string | null,
) => {
  const isDistrictComparison = navItem === "districtComparison";

  const filtersQueryKey = isDistrictComparison
    ? [
      queryKeys.vendorQuoteAssessment,
      "filters",
      userEmail,
      commodity,
      navItem,
      // Refetch filter options whenever a District Comparison filter changes.
      contractTypes,
      monthYear,
      benchmark,
    ]
    : [
      queryKeys.vendorQuoteAssessment,
      "filters",
      userEmail,
      commodity,
      contractTypes,
      contractValidity,
      normalize(district),
      normalize(vendor),
      monthYear,
      benchmark,
      quotes,
      navItem,
    ];

  const {
    data,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: filtersQueryKey,
    queryFn: () =>
      getFilters(
        userEmail,
        commodity,
        district,
        vendor,
        monthYear,
        contractTypes,
        contractValidity,
        benchmark,
        quotes,
        navItem,
      ),
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  const fetchFilters: NegotiationFilterRes = useMemo(() => {
    const result: NegotiationFilterRes = { ...(data?.data ?? {}) };

    if (result.quotation_no) {
      result.quotation_no = result.quotation_no.map((ele) => {
        let quote;

        if (ele.quotation_no == "11") {
          return {
            quotation_no: "11",
            quotation_name: "Latest Quote",
          };
        }

        if (commodity === "PVC Conduit") {
          quote = metaDataPvcConduit.quotes.find(
            (q) => q.quoteNumber == ele.quotation_no,
          );
        } else {
          quote = metaDataSteelConduit.quotes.find(
            (q) => q.quoteNumber == ele.quotation_no,
          );
        }

        return quote
          ? {
            quotation_no: quote.quoteNumber,
            quotation_name: quote.quoteName,
          }
          : {
            quotation_no: ele.quotation_no,
            quotation_name: "",
          };
      });
    }

    if (result.month_year && navItem !== 'quoteSummary') {
      result.month_year = [...result.month_year].sort((a: any, b: any) => {
        const dateA: any = new Date(a.month + " 01");
        const dateB: any = new Date(b.month + " 01");
        return dateB - dateA;
      });
    }

    if (result.month_year && navItem === 'quoteSummary' && commodity !== 'Copper Wire') {
      result.month_year = result.month_year
        .filter((item) => item?.month)
        .sort((a, b) => getMonthTimestamp(b.month) - getMonthTimestamp(a.month))
        .slice(0, 12);
    }

    return result;
  }, [data, navItem, commodity]);

  return {
    fetchFilters,
    isLoading,
    isError,
    error,
  };
};

const getFilters = (
  userEmail: string | null,
  commodity: string | null,
  district: string[] | null,
  vendor: string[] | null,
  monthYear: string | null,
  contractTypes: string[] | null,
  contractValidity: string[] | null,
  benchmark: string[] | null,
  quotes: string[] | null,
  navItem: string | null,
) => {
  const queryParams = getQueryParams(
    commodity,
    district,
    vendor,
    quotes,
    null,
    null,
    monthYear,
    contractTypes,
    contractValidity,
    benchmark,
    navItem
  );
  const newQuery =
    queryParams &&
      queryParams !== "" &&
      queryParams !== undefined &&
      queryParams !== null
      ? `${queryParams}&email=${JSON.stringify(userEmail)}`
      : `?email=${JSON.stringify(userEmail)}`;
  const res = cdFetch(`/getAllFiltersUpcomingMonthlyNegotiations${newQuery}`);
  return res;
};

export const useVendorQuoteRenegotiateData = (
  commodity: string | null,
  district: string[] | null,
  vendor: string[] | null,
  benchmark: string[] | null,
  navItem: string | null
) => {
  const { data, isLoading } = useQuery({
    queryKey: [
      queryKeys.vendorQuoteAssessment,
      "renegotiate-data",
      commodity,
      normalize(district),
      normalize(vendor),
      benchmark,
    ],
    queryFn: () => getVendorQuoteRenegotiateData(commodity, district, vendor, benchmark),
    enabled: navItem === "renegotiateSummary"
    // staleTime: 20000
  });
  const vendorData: RenegotiateVendorDetailsData = { ...data?.data };
  return { vendorData, isLoading };
};

const getVendorQuoteRenegotiateData = (
  commodity: string | null,
  district: string[] | null,
  vendor: string[] | null,
  benchmark: string[] | null,
) => {
  const queryParams = getQueryParams(commodity, district, vendor, null, null, null, null, null, null, benchmark);
  const res = cdFetch(`/getVendorQuoteRenegotiateData${queryParams}`);
  return res;
};

export const useQuoteDetailsData = (
  commodity: string | null,
  district: string[] | null,
  vendor: string[] | null,
  contractTypes: string[] | null,
  contractValidity: string[] | null,
  benchmark: string[] | null,
  navItem: string | null,
  negotiationDate?: string | null,
  weekDate?: string | null
) => {
  const { data, isLoading } = useQuery({
    queryKey: [
      queryKeys.vendorQuoteAssessment,
      "quote-details",
      commodity,
      normalize(district),
      normalize(vendor),
      contractTypes,
      contractValidity,
      benchmark,
      negotiationDate,
      weekDate,
    ],
    queryFn: () =>
      getQuoteDetails(
        commodity,
        district,
        vendor,
        contractTypes,
        contractValidity,
        benchmark,
        negotiationDate,
        weekDate,
      ),
    enabled:
      navItem === "quoteDetails" &&
      commodity !== null &&
      district !== null &&
      (commodity === "Copper Wire" ? benchmark !== null : true),
    staleTime: 2000,
  });
  const quoteDetails: QuoteSummaryRes = { ...data?.data };
  return { quoteDetails, isLoading };
};


const getQuoteDetails = (
  commodity: string | null,
  district: string[] | null,
  vendor: string[] | null,
  contractTypes: string[] | null,
  contractValidity: string[] | null,
  benchmark: string[] | null,
  negotiationDate?: string | null,
  weekDate?: string | null,
) => {
  const queryParams = getQueryParams(
    commodity,
    district,
    vendor,
    null,
    null,
    null,
    null,
    contractTypes,
    contractValidity,
    benchmark,
  );

  // negotiationDate should be appended as a query param when provided.
  const negotiationDateParam = negotiationDate
    ? `negotiation_date=${encodeURIComponent(negotiationDate)}`
    : "";

  // week_date carries the same value shown in the negotiation date label and
  // is only sent for the spot contract type.
  const weekDateParam = weekDate
    ? `week_date=${encodeURIComponent(weekDate)}`
    : "";

  const extraParams = [negotiationDateParam, weekDateParam]
    .filter(Boolean)
    .join("&");

  const hasQuery = Boolean(queryParams && queryParams.length > 0);
  const hasExtra = Boolean(extraParams);

  const url = (() => {
    if (hasQuery && hasExtra) {
      return `/getVendorQuoteDetailsData${queryParams}&${extraParams}`;
    }
    if (hasQuery) {
      return `/getVendorQuoteDetailsData${queryParams}`;
    }
    if (hasExtra) {
      return `/getVendorQuoteDetailsData?${extraParams}`;
    }
    return `/getVendorQuoteDetailsData`;
  })();

  return cdFetch(url);
};


export const useDistrictComparisonData = (
  commodity: string | null,
  monthYear: string | null,
  quotes: string | null,
  contractTypes: string[] | null,
  benchmark: string[] | null | string,
  navItem: string | null
) => {
  const { data, isLoading } = useQuery({
    queryKey: [
      queryKeys.vendorQuoteAssessment,
      "district-comparison",
      commodity,
      monthYear,
      quotes,
      contractTypes,
      benchmark
    ],
    queryFn: () => getDistrictComparison(commodity, quotes, monthYear, contractTypes, benchmark, navItem),
    enabled:
      navItem === "districtComparison" &&
      typeof commodity === "string" &&
      commodity.length > 0 &&
      typeof monthYear === "string" &&
      monthYear.length > 0
    // staleTime: 20000
  });
  const districtComparisionData: any = { ...data?.data?.data };
  return { districtComparisionData, isLoading };
};

const getDistrictComparison = (
  commodity: string | null,
  quotes: string | null,
  monthYear: string | null,
  contractTypes: string[] | null,
  benchmark: string[] | string | null,
  navItem: string | null,
) => {
  const queryParams = getQueryParams(commodity, null, null, quotes, null, null, monthYear, contractTypes, null, benchmark, navItem);
  const res = cdFetch(`/getDotchartVendorData${queryParams}`);
  return res;
};

export const useSkuRatioData = (
  commodity: string | null,
  district: string[] | null,
  vendor: string[] | null,
  quotes: any,
  type: boolean,
  type_2: boolean,
  monthYear: string | null,
  contractTypes: string[] | null,
  contractValidity: string[] | null,
  benchmark: string[] | null,
  navItem: string | null
) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      queryKeys.vendorQuoteAssessment,
      "skuratio",
      commodity,
      normalize(district),
      normalize(vendor),
      quotes,
      type,
      type_2,
      monthYear,
      contractTypes,
      contractValidity,
      benchmark
    ],
    queryFn: () =>
      getSkuRatioData(
        commodity,
        district,
        vendor,
        quotes,
        type ? "district" : "vendor",
        type_2 ? "ratio" : "quote",
        monthYear,
        contractTypes,
        contractValidity,
        benchmark,
        navItem
      ),
    staleTime: 20000,
    enabled: navItem === "sKURatioAnalysis" && vendor !== null && district !== null && monthYear !== "" && monthYear !== null && (commodity == 'Copper Wire' ? benchmark !== null : true)
  });
  const res = { ...data?.data?.data };
  return { res, isLoading, refetch };
};

export const useSkuQuoteDetailsData = (
  commodity: string | null,
  district: string[] | null,
  vendor: string[] | null,
  quotes: any,
  skuQuoteDetailsToggle: string,
  monthYear: string[] | null,
  contractTypes: string[] | null,
  contractValidity: string[] | null,
  benchmark: string[] | null,
  navItem: string | null,
  isReady: boolean = true
) => {
  const { data, isLoading } = useQuery({
    queryKey: [
      queryKeys.vendorQuoteAssessment,
      "skuratio",
      commodity,
      normalize(district),
      normalize(vendor),
      quotes,
      skuQuoteDetailsToggle,
      monthYear,
      contractTypes,
      contractValidity,
      benchmark
    ],
    queryFn: () =>
      getSkuQuoteDetailsData(
        commodity,
        district,
        vendor,
        quotes,
        skuQuoteDetailsToggle === 'supplier' ? 'vendor' : skuQuoteDetailsToggle,
        monthYear,
        contractTypes,
        contractValidity,
        benchmark,
        navItem
      ),
    staleTime: 20000,
    enabled:
      navItem === "quoteSummary" &&
      isReady &&
      vendor !== null &&
      district !== null &&
      Array.isArray(monthYear) &&
      monthYear.length > 0 &&
      (commodity === "Copper Wire" ? benchmark !== null : true)
  });
  const res = { ...data?.data?.data };
  return { res, isLoading };
};

const getSkuRatioData = (
  commodity: string | null,
  district: string[] | null,
  vendor: string[] | null,
  quotes: any,
  type: string | null,
  type_2: string | null,
  monthYear: string | null,
  contractTypes: string[] | null,
  contractValidity: string[] | null,
  benchmark: string[] | null,
  navItem: string | null,
) => {
  const queryParams = getQueryParams(
    commodity,
    type !== "vendor" ? district : district ? [district[0]] : null,
    type !== "vendor" ? (vendor ? [vendor[0]] : null) : vendor,
    quotes,
    type,
    type_2,
    monthYear,
    contractTypes,
    contractValidity,
    benchmark,
    navItem
  );
  const res = cdFetch(`/getRatioAnalysisData${queryParams}`);
  return res;
};

export const getSkuQuoteDetailsData = (
  commodity: string | null,
  district: string[] | null,
  vendor: string[] | null,
  quotes: any,
  skuQuoteDetailsToggle: string,
  monthYear: string[] | null,
  contractTypes: string[] | null,
  contractValidity: string[] | null,
  benchmark: string[] | null,
  navItem: string | null,
) => {


  let qParams = "";
  if (commodity != null && commodity.length > 0) {
    qParams += `commodity=${JSON.stringify([commodity])}&`;
  }

  if (commodity != null && commodity === 'Copper Wire') {
    qParams += `module=wire&`;
  }

  if (navItem != null && navItem === 'districtComparison') {
    qParams += `tab=${JSON.stringify(navItem)}&`;
  }

  if (contractTypes && contractTypes.length > 0) {
    const expanded = contractTypes.flatMap((t) => t === "Firm/Float" || t === "Firm,Float" ? ["Firm", "Float"] : [t?.split(" ")[0]]);
    if (navItem === 'districtComparison' && commodity === 'Copper Wire') {
      qParams += `quote_type=${JSON.stringify(expanded)}&`;
    } else {
      qParams += `contract_type=${JSON.stringify(expanded)}&`;
    }
  }

  if (benchmark && benchmark.length > 0) {
    qParams += (navItem === 'districtComparison' && commodity === 'Copper Wire') ? `base_sku=${JSON.stringify(benchmark)}&` : `benchmark=${JSON.stringify(benchmark)}&`;
  }

  if (contractValidity && contractValidity.length > 0) {
    qParams += `contract_validity=${JSON.stringify(contractValidity)}&`;
  }

  if (district && district.length > 0) {
    qParams += `district=${JSON.stringify(district)}&`;
  }

  if (vendor && vendor.length > 0) {
    qParams += `vendor=${JSON.stringify(vendor)}&`;
  }

  if (quotes && quotes.length > 0) {
    qParams += `quotation_no=${JSON.stringify(quotes)}&`;
  }

  if (skuQuoteDetailsToggle && skuQuoteDetailsToggle.length > 0) {
    qParams += `type=${JSON.stringify(skuQuoteDetailsToggle)}&`;
  }

  // For Copper Wire + Spot contract type, the "Month of"/"Week of" filter is
  // labeled "Week of" and its selected value should be sent as `week_date`
  // instead of `month_year`. The "Week of" value is a "MM-DD-YY" date string
  // (e.g. "09-28-26"), not a "Mon YY" month label, so it must be converted
  // with convertMmDdYyToDate instead of convertMonthYearToDate.
  const isCopperWireSpot =
    commodity === 'Copper Wire' &&
    Boolean(contractTypes?.some((t) => (t ?? '').toLowerCase().startsWith('spot')));
  const copperWireDateKey = isCopperWireSpot ? 'week_date' : 'month_year';
  const copperWireDateConverter = isCopperWireSpot
    ? convertMmDdYyToDate
    : convertMonthYearToDate;

  if (monthYear && monthYear?.length > 0 && skuQuoteDetailsToggle === 'month' && commodity !== 'Copper Wire') {
    qParams += `month_year=${JSON.stringify(monthYear.map((elem) => convertMonthYearToDate(elem)))}&`;
  }

  if (monthYear && monthYear?.length > 0 && skuQuoteDetailsToggle !== 'month' && commodity !== 'Copper Wire') {
    qParams += `month_year=${JSON.stringify(convertMonthYearToDate(monthYear[0]))}&`;
  }

  if (monthYear && monthYear?.length > 0 && skuQuoteDetailsToggle === 'month' && commodity === 'Copper Wire') {
    qParams += `${copperWireDateKey}=${JSON.stringify(monthYear.map((elem) => copperWireDateConverter(elem)))}&`;
  }

  if (monthYear && monthYear?.length > 0 && skuQuoteDetailsToggle !== 'month' && commodity === 'Copper Wire') {
    qParams += `${copperWireDateKey}=${copperWireDateConverter(monthYear[0])}&`;
  }

  if (qParams.length > 0) {
    qParams = "?" + qParams.slice(0, -1);
  }


  const res = cdFetch(`/getSKUQuoteDetailsData${qParams}`);
  return res;
};


export function getPurchasingConditionReportDataForExcel(
  userEmail: string | null,
  commodity: string | null,
  module: string,
  vendor: string,
  baseSKU?: string | null
) {
  let qParams = "";
  if (commodity && commodity.length > 0) {
    qParams += `commodity=${JSON.stringify(commodity)}&`;

    if (baseSKU && baseSKU.length > 0 && commodity === 'Copper Wire') {
      qParams += `base_sku=${JSON.stringify(baseSKU)}&`;
    }
  }

  if (module && module.length > 0) {
    qParams += `module=${JSON.stringify(module)}&`;
  }
  if (vendor && vendor.length > 0) {
    qParams += `vendor=${JSON.stringify(vendor)}&`;
  }
  if (userEmail && userEmail.length > 0) {
    qParams += `email=${JSON.stringify(userEmail)}&`;
  }
  if (qParams.length > 0) {
    qParams = "?" + qParams.slice(0, -1);
  }
  const res = cdFetch(`/getBoardCostDownloadData${qParams}`);
  return res;
}
